import { useState, useMemo, useCallback, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDebouncedValue } from "@/hooks/use-debounce";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, LayoutGrid, List, RefreshCw, Loader2, UserX, FileText, Eye, EyeOff } from "lucide-react";
import { useDeleteQuote, useQuote } from "@/hooks/useQuotes";
import { useFilteredQuotes, mergePipelineResults, hasActiveFilters, DEFAULT_FILTERS, DEFAULT_SORT, type QuoteFilters, type QuoteSortConfig } from "@/hooks/useFilteredQuotes";
import { NewQuoteDialog } from "@/components/quotes/NewQuoteDialog";
import { QuoteDetailPanel } from "@/components/quotes/QuoteDetailPanel";
import { QuotePipelineView } from "@/components/quotes/QuotePipelineView";
import { QuotesFilterBar } from "@/components/quotes/QuotesFilterBar";
import { QuotesListView } from "@/components/quotes/QuotesListView";
import { QuoteTemplatesView } from "@/components/quotes/QuoteTemplatesView";
import type { QuoteTemplateRow } from "@/hooks/useQuoteTemplates";
import { ContactDetailSheet } from "@/components/contacts/ContactDetailSheet";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { captureError } from "@/lib/error-message";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import type { Contact } from "@/hooks/useContacts";

const LIST_PAGE_SIZE = 50;
// Open quotes (the kanban cards) and closed quotes (lost/won: the "Show N lost"
// toggle and Won count) are fetched with separate caps, so closed rows can never
// push an open card off the board (#1005).
const PIPELINE_OPEN_PAGE_SIZE = 500;
const PIPELINE_CLOSED_PAGE_SIZE = 500;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const QuotesPage = () => {
  // ?quote=<id> deep link (staff notification emails, #990).
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkId = searchParams.get("quote");
  const [activeTab, setActiveTab] = useState("pipeline");
  const [filters, setFilters] = useState<QuoteFilters>(DEFAULT_FILTERS);
  const [sort, setSort] = useState<QuoteSortConfig>(DEFAULT_SORT);
  const [listPage, setListPage] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [startTemplate, setStartTemplate] = useState<QuoteTemplateRow | null>(null);
  const [viewingContactId, setViewingContactId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [resyncAllOpen, setResyncAllOpen] = useState(false);
  const [resyncingAll, setResyncingAll] = useState(false);
  const [showLost, setShowLost] = useState(false);
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const qc = useQueryClient();

  // Consume the deep-link param once so closing the panel or reloading
  // doesn't reopen it.
  useEffect(() => {
    if (deepLinkId === null) return;
    if (UUID_RE.test(deepLinkId)) setViewingId(deepLinkId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("quote");
        return next;
      },
      { replace: true },
    );
  }, [deepLinkId, setSearchParams]);

  const handleResyncAllSquare = async () => {
    setResyncingAll(true);
    setResyncAllOpen(false);
    const pendingToast = toast.loading("Resyncing all Square quotes… this may take a minute");
    try {
      const { data, error } = await supabase.functions.invoke("square-payments", {
        body: { action: "resync-all-square" },
      });
      if (error) throw error;
      const payload = data as {
        error?: string;
        total?: number;
        succeeded?: number;
        failed?: number;
        mismatched?: number;
        mismatches?: { quote_number: string | null }[];
      } | null;
      if (payload?.error) throw new Error(payload.error);
      const { total = 0, succeeded = 0, failed = 0, mismatched = 0, mismatches = [] } = payload ?? {};
      toast.dismiss(pendingToast);
      if (failed > 0) {
        toast.warning(`Resynced ${succeeded}/${total} — ${failed} failed (check edge function logs)`);
      } else {
        toast.success(`Resynced ${succeeded}/${total} Square quotes`);
      }
      if (mismatched > 0) {
        const names = mismatches.map((m) => m.quote_number).filter(Boolean).slice(0, 5).join(", ");
        const more = mismatched > 5 ? ", …" : "";
        toast.warning(
          `${mismatched} ${mismatched === 1 ? "quote doesn't" : "quotes don't"} add up to Square's total` +
            `${names ? ` (${names}${more})` : ""} — check edge function logs`,
        );
      }
      await qc.invalidateQueries({ queryKey: ["quotes"] });
    } catch (err) {
      toast.dismiss(pendingToast);
      const msg = await edgeFunctionErrorMessage(err, "Resync all failed");
      captureError(err, "quotes_square_resync_all", msg);
      toast.error(msg);
    } finally {
      setResyncingAll(false);
    }
  };

  // Stable callbacks so memoized child views (QuotePipelineView, list rows)
  // don't re-render on unrelated parent state changes.
  const handleViewQuote = useCallback((id: string) => setViewingId(id), []);
  const handleContactClick = useCallback((id: string) => setViewingContactId(id), []);
  const handleUseTemplate = useCallback((t: QuoteTemplateRow) => {
    setStartTemplate(t);
    setFormOpen(true);
  }, []);
  const closeNewQuote = useCallback(() => {
    setFormOpen(false);
    setStartTemplate(null);
  }, []);
  // The new quote is a draft, so the drawer opens on it in edit mode (#1559).
  const handleQuoteCreated = useCallback((id: string) => {
    closeNewQuote();
    setViewingId(id);
  }, [closeNewQuote]);

  // Reset page when filters change
  const handleFiltersChange = useCallback((f: QuoteFilters) => {
    setFilters(f);
    setListPage(0);
    setSelectedIds(new Set());
  }, []);

  // Debounce just the free-text search; every other filter is a discrete
  // click so firing immediately is fine and expected.
  const debouncedSearch = useDebouncedValue(filters.search, 300);
  const queryFilters = useMemo(
    () => ({ ...filters, search: debouncedSearch }),
    [filters, debouncedSearch]
  );

  // List view query
  const listQuery = useFilteredQuotes({
    filters: queryFilters,
    sort,
    page: listPage,
    pageSize: LIST_PAGE_SIZE,
    enabled: activeTab === "list",
  });

  // Pipeline view queries — no stage filter (stages are columns). Open and
  // closed quotes are fetched separately so each set has its own cap.
  const pipelineFilters = useMemo(
    () => ({
      ...queryFilters,
      stages: [] as QuoteFilters["stages"],
    }),
    [queryFilters]
  );
  const openPipelineQuery = useFilteredQuotes({
    filters: pipelineFilters,
    sort: { column: "created_at", ascending: false },
    page: 0,
    pageSize: PIPELINE_OPEN_PAGE_SIZE,
    pipelineSet: "open",
    enabled: activeTab === "pipeline",
  });
  const closedPipelineQuery = useFilteredQuotes({
    filters: pipelineFilters,
    sort: { column: "created_at", ascending: false },
    page: 0,
    pageSize: PIPELINE_CLOSED_PAGE_SIZE,
    pipelineSet: "closed",
    enabled: activeTab === "pipeline",
  });
  const pipelineData = useMemo(
    () => mergePipelineResults(openPipelineQuery.data, closedPipelineQuery.data),
    [openPipelineQuery.data, closedPipelineQuery.data]
  );
  const pipelineLoading = openPipelineQuery.isLoading || closedPipelineQuery.isLoading;

  const currentData = activeTab === "list" ? listQuery.data : pipelineData;
  const isLoading = activeTab === "list" ? listQuery.isLoading : pipelineLoading;

  // Viewing quote from either dataset
  const allQuotes = currentData?.quotes ?? [];
  const listedViewing = allQuotes.find((q) => q.id === viewingId) ?? null;
  // A deep-linked (or contact-sheet) quote may not be on the loaded page, so
  // fetch it by id in that case.
  const { data: fetchedViewing } = useQuote(listedViewing ? null : viewingId);
  const viewing = listedViewing ?? (fetchedViewing?.id === viewingId ? fetchedViewing : null);

  // Contact detail
  const { data: viewingContact } = useQuery({
    queryKey: ["contact_detail", viewingContactId],
    enabled: !!viewingContactId,
    queryFn: async () => {
      const { data, error } = await supabase.from("contacts").select("*").eq("id", viewingContactId!).single();
      if (error) throw error;
      return data as Contact;
    },
  });

  // Incomplete wizard leads (#226): name + email captured but never submitted.
  // Sales lives on this screen now (#236), so the count surfaces here; the
  // events route (interim ops view) holds the full list via its toggle.
  const { data: partialCount = 0 } = useQuery({
    queryKey: ["partial_lead_count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("events")
        .select("id", { count: "exact", head: true })
        .eq("is_partial", true)
        .eq("status", "new");
      if (error) throw error;
      return count ?? 0;
    },
    refetchInterval: 60_000,
  });

  const isEmpty = !isLoading && (currentData?.totalCount ?? 0) === 0 && !hasActiveFilters(filters);
  const lostCount = useMemo(
    () => (pipelineData?.quotes ?? []).filter((q) => q.is_lost).length,
    [pipelineData],
  );
  const leadsLabel = `${partialCount} unfinished lead${partialCount === 1 ? "" : "s"}`;

  // Tabs + unfinished-leads chip lead the toolbar row (#708). Tab labels and
  // the chip text are hidden below md so the row fits one line on a phone.
  const toolbarLeading = (
    <>
      <TabsList className="h-9 shrink-0">
        <TabsTrigger value="pipeline" aria-label="Pipeline" className="gap-1.5 px-2 py-1 md:px-3">
          <LayoutGrid className="h-3.5 w-3.5" />
          <span className="hidden md:inline">Pipeline</span>
        </TabsTrigger>
        <TabsTrigger value="list" aria-label="List" className="gap-1.5 px-2 py-1 md:px-3">
          <List className="h-3.5 w-3.5" />
          <span className="hidden md:inline">List</span>
        </TabsTrigger>
        <TabsTrigger value="templates" aria-label="Templates" className="gap-1.5 px-2 py-1 md:px-3">
          <FileText className="h-3.5 w-3.5" />
          <span className="hidden md:inline">Templates</span>
        </TabsTrigger>
      </TabsList>
      {partialCount > 0 && (
        <Button
          variant="outline"
          size="sm"
          asChild
          className="relative h-9 w-9 shrink-0 p-0 md:w-auto md:px-3"
          title="Wizard visitors who left their name + email but didn't finish — worth a manual follow-up"
        >
          <Link to="/admin/events?incomplete=1" aria-label={`${leadsLabel} — not in pipeline yet`}>
            <UserX className="h-3.5 w-3.5 md:mr-1.5" />
            <span className="hidden md:inline">{leadsLabel}</span>
            <span className="absolute -top-2 -right-2 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] text-primary-foreground md:hidden">
              {partialCount > 99 ? "99+" : partialCount}
            </span>
          </Link>
        </Button>
      )}
    </>
  );

  const lostToggle = activeTab === "pipeline" && lostCount > 0 && (
    <Button
      variant="ghost"
      size="sm"
      className="h-8 text-xs text-muted-foreground hover:text-foreground"
      onClick={() => setShowLost(!showLost)}
    >
      {showLost ? <EyeOff className="h-3.5 w-3.5 mr-1.5" /> : <Eye className="h-3.5 w-3.5 mr-1.5" />}
      {showLost ? "Hide" : "Show"} {lostCount} lost
    </Button>
  );

  const resyncButton = isAdmin && (
    <Button
      variant="outline"
      size="sm"
      onClick={() => setResyncAllOpen(true)}
      disabled={resyncingAll}
    >
      {resyncingAll ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <RefreshCw className="h-4 w-4 mr-2" />
      )}
      Resync Square
    </Button>
  );

  return (
    <DashboardLayout
      title="Pipeline"
      actions={
        <Button size="sm" onClick={() => setFormOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" />
          New Quote
        </Button>
      }
    >
      <div className="space-y-2 pb-20">
        <AlertDialog open={resyncAllOpen} onOpenChange={setResyncAllOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Resync all Square quotes?</AlertDialogTitle>
              <AlertDialogDescription>
                This will re-pull every Square-sourced quote from Square and
                overwrite its totals, tax, and line items to match what Square
                currently shows. User-managed fields (event date, notes, quote
                number) are preserved. Takes up to ~2 minutes.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleResyncAllSquare}>
                Resync all
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedIds(new Set()); }} className="space-y-2">
          {/* The filter bar only shows with quotes to filter; the tabs and chip always do. */}
          {(activeTab === "templates" || isEmpty) && (
            <div className="flex flex-wrap items-center gap-2">{toolbarLeading}</div>
          )}

          {/* The quote queries are disabled on the Templates tab, so isEmpty says nothing there. */}
          {activeTab !== "templates" && (
            isEmpty ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="rounded-full bg-muted p-4 mb-4">
                    <Plus className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-display text-lg font-semibold mb-1">No quotes yet</h3>
                  <p className="text-muted-foreground text-sm max-w-md">
                    Create your first quote to start tracking your pipeline.
                  </p>
                  <Button className="mt-4" onClick={() => setFormOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Quote
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Shared filter bar */}
                <QuotesFilterBar
                  filters={filters}
                  onChange={handleFiltersChange}
                  hideStageFilter={activeTab === "pipeline"}
                  totalCount={currentData?.totalCount}
                  leading={toolbarLeading}
                  trailing={lostToggle}
                  panelExtra={resyncButton}
                />

                <TabsContent value="pipeline" className="mt-0">
                  {pipelineLoading ? (
                    <div className="text-center py-12 text-muted-foreground">Loading...</div>
                  ) : (
                    <QuotePipelineView
                      quotes={pipelineData?.quotes ?? []}
                      onViewQuote={handleViewQuote}
                      onContactClick={handleContactClick}
                      showLost={showLost}
                    />
                  )}
                </TabsContent>

                <TabsContent value="list" className="mt-0">
                  <QuotesListView
                    quotes={listQuery.data?.quotes ?? []}
                    totalCount={listQuery.data?.totalCount ?? 0}
                    page={listPage}
                    pageSize={LIST_PAGE_SIZE}
                    sort={sort}
                    onPageChange={setListPage}
                    onSortChange={(s) => { setSort(s); setListPage(0); }}
                    onViewQuote={handleViewQuote}
                    onContactClick={handleContactClick}
                    selectedIds={selectedIds}
                    onSelectionChange={setSelectedIds}
                    isLoading={listQuery.isLoading}
                  />
                </TabsContent>
              </>
            )
          )}

          <TabsContent value="templates" className="mt-0">
            <QuoteTemplatesView onUseTemplate={handleUseTemplate} />
          </TabsContent>
        </Tabs>
      </div>

      <NewQuoteDialog
        open={formOpen}
        initialTemplate={startTemplate}
        onClose={closeNewQuote}
        onCreated={handleQuoteCreated}
      />
      <QuoteDetailPanel
        quote={viewing}
        open={!!viewing}
        onOpenChange={(o) => !o && setViewingId(null)}
        onContactClick={(id) => setViewingContactId(id)}
      />
      <ContactDetailSheet
        contact={viewingContact ?? null}
        open={!!viewingContactId}
        onOpenChange={(o) => { if (!o) setViewingContactId(null); }}
        onQuoteClick={(id) => { setViewingContactId(null); setViewingId(id); }}
      />
    </DashboardLayout>
  );
};

export default QuotesPage;
